import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal, wanneer } from '../i18n.js';
import { St } from '../components/Status.jsx';
import Stapelstaven from '../components/Stapelstaven.jsx';
import Gemaskeerd from '../components/Gemaskeerd.jsx';
import Balken from '../components/Balken.jsx';
import Periodes from '../components/Periodes.jsx';
import Lijngrafiek from '../components/Lijngrafiek.jsx';

/**
 * Mail (docs/besluiten/esa-koppeling.md): wat de Cisco ESA met de mail deed. Bovenaan de grafiek per tijdvak in ADTS-blauw,
 * zoals de alerts op het overzicht: van schoon (licht) via spam en geweigerd op reputatie naar een dreiging (donker; in
 * donkere modus andersom, zodat een dreiging het meest opvalt). Dan de soorten dreiging, de afzenders en landen, en de
 * laatste berichten met een dreiging: rood als ze toch zijn afgeleverd.
 */
const reeksenVan = () => [
  { sleutel: 'schoon', label: t('schoon'), kleur: 'var(--sev-laag)' },
  { sleutel: 'spam', label: t('spam'), kleur: 'var(--sev-midden)' },
  { sleutel: 'geweigerd', label: t('geweigerd op reputatie'), kleur: 'var(--sev-hoog)' },
  { sleutel: 'dreiging', label: t('dreiging'), kleur: 'var(--sev-kritiek)' },
];
const soortNaam = (x) => ({ malware: t('malware'), phishing: t('phishing'), url: t('kwade URL'), sdr: t('slechte reputatie afzenderdomein (SDR)'), outbreak: 'Outbreak Filters' }[x] ?? x);
const ACTIE = { afgeleverd: 'afgeleverd', quarantaine: 'quarantaine', gedropt: 'gedropt', bounce: 'teruggestuurd', afgebroken: 'afgebroken', geweigerd: 'geweigerd',
  herschreven: 'herschreven' };
/* De filter boven de laatste berichten met een dreiging (Bernhard, 2026-10-26). */
const filters = () => [['geweigerd', t('Geweigerd')], ['afgeleverd', t('Afgeleverd')], ['herschreven', t('Herschreven')], ['quarantaine', t('Quarantaine')]];

export default function Mail({ klant }) {
  const [dagen, zetDagen] = useState(7);
  const [actie, zetActie] = useState(null);
  const q = useQuery({ queryKey: ['grafiek', 'mail', klant, dagen], queryFn: () => api.grafiek('mail', klant, dagen), refetchInterval: 60_000 });
  const gefilterd = useQuery({ queryKey: ['grafiek', 'mail', klant, dagen, actie], queryFn: () => api.mailDreigingen(klant, dagen, actie),
    /* Vorige data alleen bij dezelfde klant (de klant staat op index 2 van de sleutel). */
    refetchInterval: 60_000, enabled: Boolean(actie), placeholderData: (vorige, q) => (q?.queryKey?.[2] === klant ? vorige : undefined) });
  const d = q.data?.data;
  const reeksen = reeksenVan();
  const tegel = (n, label, extra = '') => <div className={`tile ${extra}`}><div className="n">{n}</div><div className="t">{label}</div></div>;
  const kop = (
    <>
      <div className="page-head"><div>
        <h1>Email Security</h1>
        <p className="sub">{t('Wat de Cisco ESA tegenhield of afleverde.')}</p>
      </div></div>
      <div className="filters"><Periodes keuzes={[1, 7, 30]} waarde={dagen} onKies={zetDagen} /></div>
    </>
  );
  if (q.isPending) return <>{kop}<p className="muted">{t('Laden…')}</p></>;
  if (q.isError) return <>{kop}<div className="melder rood">{t(q.error.message)}</div></>;
  if (!d.totals.binnen && !d.totals.buiten && !d.totals.verbindingen) {
    return <>{kop}<section className="card"><p className="empty">{t('Nog geen mail uit een Cisco ESA. Voeg de ESA toe onder Bronnen en stel de log subscriptions in met de stappen bij de bron.')}</p></section></>;
  }
  return (
    <>
      {kop}
      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{{ hour: t('Per uur'), '6 hours': t('Per zes uur') }[d.per] ?? t('Per dag')}</h2>
          <span className="meta legenda">{reeksen.slice().reverse().map((r) => <span key={r.sleutel}><i style={{ background: r.kleur }} />{r.label}</span>)}</span></header>
        <Stapelstaven punten={d.tijd} reeksen={reeksen} perUur={d.per !== 'day'} label={t('Mail per tijdvak')} />
      </section>
      <div className="tiles">
        {tegel(getal(d.totals.binnen), t('berichten binnen'))}
        {tegel(getal(d.totals.spam), t('spam'))}
        {tegel(getal(d.totals.dreiging), t('met een dreiging, {q} in quarantaine', { q: getal(d.totals.quarantaine) }), d.totals.dreiging ? 'warn' : '')}
        {tegel(getal(d.totals.afgeleverd), t('met een dreiging toch afgeleverd'), d.totals.afgeleverd ? 'crit' : '')}
      </div>
      <section className="card" style={{ marginBottom: 18 }}>
        <header><h2>{t('Mailauthenticatie')}</h2><span className="meta">{t('binnenkomende mail, deze periode')}</span></header>
        <div className="mailauth">
          {['spf', 'dkim', 'dmarc'].map((p) => { const a = d.auth[p]; return (
            <div key={p} className="mailauth-rij"><b>{p.toUpperCase()}</b>
              {a.total ? <span>{getal(a.pass)} {t('pass')}{a.fail ? <> · <span className="fout">{getal(a.fail)} {t('fail')}</span></> : null}</span>
                : <span className="muted">{t('niet door de ESA gecontroleerd')}</span>}</div>
          ); })}
        </div>
        {(!d.auth.dmarc.total || !d.auth.dkim.total) && <p className="noot">{t('DMARC- en/of DKIM-verificatie staan mogelijk uit op de ESA. Zet ze aan om spoofing en vervalste afzenders te zien.')}</p>}
      </section>
      <Wachtrijen w={d.wachtrij} perDag={d.perLijn !== 'hour'} />
      <div className="grid-2" style={{ marginBottom: 18 }}>
        <section className="card"><header><h2>{t('Soorten dreiging')}</h2></header>
          <Balken rijen={d.soorten.map((s) => ({ naam: soortNaam(s.naam), n: s.n, extra: s.afgeleverd ? t('{n} afgeleverd', { n: getal(s.afgeleverd) }) : null }))} /></section>
        <section className="card"><header><h2>{t('Afzenders van dreigingen')}</h2><span className="meta">{t('domein')}</span></header>
          <Balken rijen={d.afzenders} /></section>
        <section className="card"><header><h2>{t('Geweigerd op reputatie')}</h2><span className="meta">{t('{n} verbindingen, per land', { n: getal(d.totals.geweigerd) })}</span></header>
          <Balken rijen={d.landen} /></section>
        <section className="card"><header><h2>{t('Uitgaand')}</h2></header>
          <p className="soft" style={{ margin: 0 }}>{t('{n} berichten verstuurd in deze periode.', { n: getal(d.totals.buiten) })}</p>
          <p className="noot">{t('Een eigen account dat malware of phishing verstuurt, geeft een kritiek alert.')}</p></section>
      </div>
      <section className="card">
        <header><h2>{t('Laatste berichten met een dreiging')}</h2></header>
        <div className="filters" style={{ marginBottom: 8 }}>
          <button type="button" className="chip" aria-pressed={!actie} onClick={() => zetActie(null)}>
            {t('Alle ({n})', { n: getal(Object.values(d.perActie ?? {}).reduce((s, n) => s + n, 0)) })}</button>
          {filters().map(([k, label]) => (
            <button key={k} type="button" className="chip" aria-pressed={actie === k} onClick={() => zetActie(actie === k ? null : k)}>
              {label} <span className="muted">{getal(d.perActie?.[k] ?? 0)}</span></button>
          ))}
        </div>
        {(() => { const lijst = actie ? gefilterd.data?.data.laatste : d.laatste; return actie && !lijst ? <p className="muted">{t('Laden…')}</p> : lijst.length ? (
          <table>
            <thead><tr><th>{t('Wanneer')}</th><th>{t('Van en aan')}</th><th className="hide-sm">{t('Onderwerp')}</th><th>{t('Dreiging')}</th><th>{t('Gebeurd')}</th></tr></thead>
            <tbody>{lijst.map((m, i) => (
              <tr key={i}>
                <td className="mono muted">{wanneer(m.at)}</td>
                <td style={{ fontSize: 13 }}><Gemaskeerd veld={m.mail_from} mono />
                  {m.ip && <span className="muted"> · {m.ip}{m.country ? `, ${m.country}` : ''}</span>}<br />
                  <span className="muted">→ {m.rcpt?.length ? m.rcpt.map((x, j) => <span key={j}>{j > 0 ? ', ' : ''}<Gemaskeerd veld={x} /></span>) : '—'}{m.ontvangers > 3 ? ` +${m.ontvangers - 3}` : ''}</span>
                  {(m.spf || m.dkim || m.dmarc) && <><br /><span className="mailauth-mini">{['spf', 'dkim', 'dmarc'].filter((a) => m[a]).map((a) => <span key={a} className={m[a] === 'pass' ? 'ok' : 'fout'}>{a.toUpperCase()} {m[a]}</span>)}</span></>}</td>
                <td className="hide-sm" style={{ fontSize: 13, overflowWrap: 'anywhere' }}>{m.subject ?? '—'}</td>
                <td style={{ fontSize: 13 }}><b>{soortNaam(m.threat)}</b>{m.wat && <><br /><span className="muted mono" style={{ overflowWrap: 'anywhere' }}>{m.wat}</span></>}</td>
                <td>{m.action === 'afgeleverd' ? <St soort="critical">{t('afgeleverd')}</St>
                  : m.action ? <St soort="good">{t(ACTIE[m.action] ?? m.action)}{m.quarantine ? ` · ${m.quarantine}` : ''}</St> : <span className="muted">{t('bezig')}</span>}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <p className="empty">{t('Geen berichten met een dreiging in deze periode.')}</p>; })()}
        <p className="noot">{t('Van schone mail haalt ADTS ZERO na een dag de ontvangers en het onderwerp weg, van de afzender blijft het domein. Berichten met een dreiging blijven 90 dagen.')}</p>
      </section>
    </>
  );
}

/**
 * De wachtrijen van de ESA, uit de Status Logs: de laatste stand per ESA, en het hoogste per tijdvak. Twee grafieken naast
 * elkaar (berichten en ontvangers zijn geen zelfde grootheid), elk met een eigen as. Vol: het alert esa-wachtrij.
 */
function Wachtrijen({ w, perDag }) {
  if (!w?.nu.length && !w?.tijd.some((p) => p.workq || p.actvrcp)) return null;
  return (
    <section className="card" style={{ marginBottom: 18 }}>
      <header><h2>{t('Wachtrijen')}</h2><span className="meta">{t('het hoogste per tijdvak')}</span></header>
      {w.nu.map((b) => (
        <p key={b.bron} className="soft" style={{ margin: '0 0 10px' }}>
          <b>{b.bron}</b> · {t('nu: werkwachtrij {workq}, afleverwachtrij {actvrcp} ontvangers, op schijf {vol}% vol, {q} in quarantaine',
            { workq: getal(b.workq ?? 0), actvrcp: getal(b.actvrcp ?? 0), vol: getal(b.vol ?? 0), q: getal(b.quarmsgs ?? 0) })}
          {b.rescon > 0 && <> · <St soort="critical">{t('resource conservation')}</St></>}
        </p>
      ))}
      <div className="grid-2">
        <div><h3 style={{ fontSize: 13, margin: "0 0 6px" }}>{t('Werkwachtrij (WorkQ)')}</h3>
          <Lijngrafiek punten={w.tijd.map((p) => ({ van: p.van, n: p.workq }))} perDag={perDag} hoogte={160} eenheid={t('berichten')} label={t('Werkwachtrij')} /></div>
        <div><h3 style={{ fontSize: 13, margin: "0 0 6px" }}>{t('Afleverwachtrij (ActvRcp)')}</h3>
          <Lijngrafiek punten={w.tijd.map((p) => ({ van: p.van, n: p.actvrcp }))} perDag={perDag} hoogte={160} eenheid={t('ontvangers')} label={t('Afleverwachtrij')} /></div>
      </div>
      <p className="noot">{t('Een alert als een wachtrij tien minuten boven de grens blijft (500 berichten, 2.000 ontvangers, of de wachtrij op schijf half vol), met een melding als het weer normaal is.')}</p>
    </section>
  );
}
